import React, { useState } from 'react';
import { Box, Typography, Grid, Alert, IconButton, Chip, Button } from '@mui/joy';
import { Link } from 'react-router-dom';
import { ArrowsClockwise as RefreshIcon } from '@phosphor-icons/react';
import {
  useAutoStormTerritories,
  useSubscribeToTerritory,
  useUnsubscribeFromTerritory,
} from '../../hooks/useStormTracker';
import { useAuth } from '../../contexts/AuthContext';
import { TerritoryCard } from '../../components/stormtracker/TerritoryCard';

const TerritoriesList: React.FC = () => {
  const [subscribingId, setSubscribingId] = useState<number | null>(null);

  const { user, loading: authLoading } = useAuth();
  const { data: territories, isLoading, error, refetch, isFetching } = useAutoStormTerritories(user);
  const [actionError, setActionError] = useState('');
  const subscribeMutation = useSubscribeToTerritory();
  const unsubscribeMutation = useUnsubscribeFromTerritory();

  const handleSubscribe = async (id: number) => {
    setActionError('');
    setSubscribingId(id);
    try {
      await subscribeMutation.mutateAsync(id);
    } catch {
      setActionError('Unable to update alert subscription. Please try again.');
    } finally {
      setSubscribingId(null);
    }
  };

  const handleUnsubscribe = async (id: number) => {
    setActionError('');
    setSubscribingId(id);
    try {
      await unsubscribeMutation.mutateAsync(id);
    } catch {
      setActionError('Unable to update alert subscription. Please try again.');
    } finally {
      setSubscribingId(null);
    }
  };

  const autoTerritories = (territories || []).filter((t) => t.is_auto_generated);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography level="h3">Your storm watchlists</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>

        <IconButton aria-label="Refresh watchlists" variant="outlined" color="neutral" loading={isFetching} onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
        </Box>
      </Box>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>Watchlists collect ZIP codes from qualifying leads. Choose a watchlist to review activity and manage alerts.</Typography>

      {(error || actionError) && <Alert color="danger" sx={{ mb: 2 }}>{actionError || 'Unable to load watchlists. Use Refresh watchlists to try again.'}</Alert>}
      {authLoading || isLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid key={i} xs={12} sm={6} md={4}>
              <Box
                sx={{
                  height: 200,
                  bgcolor: 'background.level1',
                  borderRadius: 'md',
                  animation: 'pulse 1.5s infinite',
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : !error && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography level="h4">{user?.is_organization_admin ? 'Team watchlists' : 'My watchlist'}</Typography>
            <Chip size="sm" variant="soft">{autoTerritories.length}</Chip>
          </Box>
          {autoTerritories.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 3,
                bgcolor: 'background.level1',
                borderRadius: 'lg',
                mb: 4,
              }}
            >
              <Typography level="h4" sx={{ mb: 1 }}>
                Your watchlist is empty
              </Typography>
              <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                Start with a lead: mark a positive door knock as needing follow-up, or move an opportunity into a stage with Storm Tracker enabled. Its ZIP code is added here automatically.
              </Typography>
              <Button component={Link} to="/crm/pipeline" sx={{ mt: 2 }}>Open sales pipeline</Button>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {autoTerritories.map((territory) => (
                <Grid key={territory.id} xs={12} sm={6} md={4}>
                  <TerritoryCard
                    territory={territory}
                    onSubscribe={handleSubscribe}
                    onUnsubscribe={handleUnsubscribe}
                    isSubscribing={subscribingId === territory.id}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default TerritoriesList;
